import { useEffect, useRef, useState } from "react";

/**
 * Tracks which incidents have a "fresh" activity stamp (new job or update) within the last `durationMs`.
 * Returns a Set<incidentId> currently flashing.
 */
export default function useIncidentActivity(incidents, { durationMs = 8000, ignoreInitial = true } = {}) {
  const lastStampRef = useRef(new Map()); // id -> last seen (updated_at || created_at)
  const flashUntilRef = useRef(new Map()); // id -> wall-time when flash ends
  const [, setTick] = useState(0);

  // Scan incidents on each change
  useEffect(() => {
    const now = Date.now();
    const firstScan = lastStampRef.current.size === 0;
    let changed = false;
    for (const inc of incidents) {
      const stamp = inc.updated_at || inc.created_at || "";
      const prev = lastStampRef.current.get(inc.id);
      lastStampRef.current.set(inc.id, stamp);
      // Don't flash everything the very first time the panel loads.
      if (ignoreInitial && firstScan) continue;
      if (prev !== stamp) {
        flashUntilRef.current.set(inc.id, now + durationMs);
        changed = true;
      }
    }
    if (changed) setTick((t) => t + 1);
  }, [incidents, durationMs, ignoreInitial]);

  // Periodic tick to expire flashes
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      let any = false;
      for (const [id, until] of flashUntilRef.current.entries()) {
        if (until <= now) {
          flashUntilRef.current.delete(id);
          any = true;
        }
      }
      if (any) setTick((x) => x + 1);
    }, 600);
    return () => clearInterval(t);
  }, []);

  const active = new Set();
  const now = Date.now();
  for (const [id, until] of flashUntilRef.current.entries()) {
    if (until > now) active.add(id);
  }
  return active;
}
